import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_APPLY_MANAGER } from "@/lib/seo-defs";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Loader2, Plus, Trash2, User as UserIcon, BriefcaseBusiness, GraduationCap, MapPin, Award, Users as UsersIcon, FileSignature, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AvatarUpload, CvUpload } from "@/components/MediaUpload";

const MANAGER_ROLES = [
  "Fractional CEO / General Manager",
  "Fractional COO / Operations Manager",
  "Fractional CFO / Finance Manager",
  "Fractional CTO / Digital Transformation Manager",
  "Fractional CMO / Marketing & Sales Manager",
  "Fractional CHRO / HR & Organization Manager",
  "Fractional CSO / Strategy Manager",
  "Sustainability & Energy Manager",
  "Project Management Officer",
  "Procurement & Supply Chain Manager",
  "Export & Internationalization Manager",
  "Quality, Compliance & Risk Manager",
  "Engineering Manager",
  "Facility & Asset Manager",
  "Business Development Manager",
  "Change Management Manager",
];

const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia",
  "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const COMPANY_SIZES = [
  "Micro (1-9 dipendenti)",
  "PMI (10-49 dipendenti)",
  "Small Mid (50-249 dipendenti)",
  "Mid-Market (250-999 dipendenti)",
  "Large Enterprise (1000+ dipendenti)",
];

const LANGUAGES_AVAILABLE = ["Italiano", "Inglese", "Francese", "Spagnolo", "Tedesco", "Portoghese", "Cinese mandarino", "Russo", "Arabo"];

const COVERAGE_ALL_ITALY = "Tutta Italia";
const COVERAGE_ABROAD = "Estero";

const MAX_SECONDARY = 3;
const MAX_EXPERIENCES = 5;
const MAX_REFERENCES = 3;

const initialExperience = () => ({ company: "", role: "", from: "", to: "", sector: "", result: "" });
const initialReference = () => ({ name: "", role_company: "", email: "" });

export default function ApplyManagerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({
    title: "",
    bio: "",
    primary_role: "",
    secondary_roles: [],
    industries: [],
    skills: [],
    problems_solved: [],
    company_sizes_handled: [],
    languages: ["Italiano"],
    years_experience: 0,
    years_managerial: 0,
    monthly_availability_days: 8,
    max_concurrent_clients: 3,
    travel_availability: "",
    work_modes: [],
    daily_rate_min: "",
    daily_rate_max: "",
    coverage_areas: [],
    city: "",
    region: "",
    linkedin: "",
    website: "",
    photo: null, // { id, name, download_url, ... }
    cv_url: null, // { id, name, download_url, ... }
    experiences_summary: "",
    case_studies: [], // [{ company, role, from, to, sector, result }]
    references: [], // [{ name, role_company, email }]
    consent_privacy: false,
    consent_ethics: false,
    conflict_declaration: false,
    application_status: "draft",
  });
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    if (user?.role === "manager") {
      api
        .get("/managers/me")
        .then((r) => {
          const remote = r.data || {};
          setData((d) => ({
            ...d,
            ...remote,
            // ensure arrays exist
            secondary_roles: remote.secondary_roles || [],
            industries: remote.industries || [],
            skills: remote.skills || [],
            problems_solved: remote.problems_solved || [],
            company_sizes_handled: remote.company_sizes_handled || [],
            languages: remote.languages?.length ? remote.languages : ["Italiano"],
            work_modes: remote.work_modes || [],
            coverage_areas: remote.coverage_areas || [],
            case_studies: remote.case_studies || [],
            references: remote.references || [],
          }));
        })
        .catch(() => {});
    }
  }, [user]);

  // === helpers ===
  const toggleArr = (key, val) => {
    setData((d) => {
      const arr = d[key] || [];
      return { ...d, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const toggleSecondary = (role) => {
    setData((d) => {
      const arr = d.secondary_roles || [];
      if (arr.includes(role)) return { ...d, secondary_roles: arr.filter((x) => x !== role) };
      if (arr.length >= MAX_SECONDARY) {
        toast.error(`Massimo ${MAX_SECONDARY} ruoli secondari`);
        return d;
      }
      return { ...d, secondary_roles: [...arr, role] };
    });
  };

  const toggleCoverage = (area) => {
    setData((d) => {
      const arr = d.coverage_areas || [];
      let next;
      if (arr.includes(area)) {
        next = arr.filter((x) => x !== area);
      } else if (area === COVERAGE_ALL_ITALY) {
        // selecting "Tutta Italia" removes individual regions
        next = [...arr.filter((x) => !ITALIAN_REGIONS.includes(x)), COVERAGE_ALL_ITALY];
      } else if (ITALIAN_REGIONS.includes(area)) {
        // selecting a region removes "Tutta Italia" exclusivity
        next = [...arr.filter((x) => x !== COVERAGE_ALL_ITALY), area];
      } else {
        next = [...arr, area];
      }
      return { ...d, coverage_areas: next };
    });
  };

  // === experiences (case_studies) ===
  const addExperience = () => {
    setData((d) => {
      if ((d.case_studies || []).length >= MAX_EXPERIENCES) {
        toast.error(`Massimo ${MAX_EXPERIENCES} esperienze chiave`);
        return d;
      }
      return { ...d, case_studies: [...(d.case_studies || []), initialExperience()] };
    });
  };
  const updateExperience = (idx, key, val) => {
    setData((d) => {
      const arr = [...(d.case_studies || [])];
      arr[idx] = { ...arr[idx], [key]: val };
      return { ...d, case_studies: arr };
    });
  };
  const removeExperience = (idx) => {
    setData((d) => ({ ...d, case_studies: (d.case_studies || []).filter((_, i) => i !== idx) }));
  };

  // === references ===
  const addReference = () => {
    setData((d) => {
      if ((d.references || []).length >= MAX_REFERENCES) {
        toast.error(`Massimo ${MAX_REFERENCES} referenze`);
        return d;
      }
      return { ...d, references: [...(d.references || []), initialReference()] };
    });
  };
  const updateReference = (idx, key, val) => {
    setData((d) => {
      const arr = [...(d.references || [])];
      arr[idx] = { ...arr[idx], [key]: val };
      return { ...d, references: arr };
    });
  };
  const removeReference = (idx) => {
    setData((d) => ({ ...d, references: (d.references || []).filter((_, i) => i !== idx) }));
  };

  // === save / submit ===
  const buildPayload = () => {
    const payload = { ...data };
    if (payload.daily_rate_min === "") delete payload.daily_rate_min;
    else if (payload.daily_rate_min != null) payload.daily_rate_min = parseInt(payload.daily_rate_min);
    if (payload.daily_rate_max === "") delete payload.daily_rate_max;
    else if (payload.daily_rate_max != null) payload.daily_rate_max = parseInt(payload.daily_rate_max);
    return payload;
  };

  const save = async (silent = false) => {
    setSavingDraft(true);
    try {
      await api.put("/managers/me", buildPayload());
      if (!silent) toast.success("Bozza salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingDraft(false);
    }
  };

  const submit = async () => {
    if (!data.consent_privacy || !data.consent_ethics) {
      toast.error("Consensi privacy e codice etico obbligatori");
      return;
    }
    if (!data.primary_role) {
      toast.error("Seleziona un ruolo principale prima di inviare");
      return;
    }
    setLoading(true);
    try {
      await save(true);
      await api.post("/managers/me/submit");
      toast.success("Candidatura inviata al team CXOORA!");
      navigate("/manager");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio");
    } finally {
      setLoading(false);
    }
  };

  // === guards ===
  if (!user) {
    return (
      <div className="min-h-screen">
        <SEO {...SEO_APPLY_MANAGER} />
        <PublicHeader />
        <section className="pt-28 pb-12 bg-[#F6F8FC] min-h-[calc(100vh-200px)]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 cxo-card p-8 text-center">
            <h1 className="font-display text-2xl font-bold text-slate-900">Candidati al network CXOORA</h1>
            <p className="mt-3 text-slate-600">Per iniziare la candidatura crea un account manager.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register"><Button className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white">Registrati come manager</Button></Link>
              <Link to="/login"><Button variant="outline">Ho già un account</Button></Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (user.role !== "manager") {
    return (
      <div className="min-h-screen">
        <PublicHeader />
        <section className="pt-28 pb-12 bg-[#F6F8FC]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 cxo-card p-8 text-center">
            <h1 className="font-display text-2xl font-bold text-slate-900">Solo manager possono candidarsi</h1>
            <p className="mt-3 text-slate-600">Devi essere registrato come manager. Esci e crea un nuovo account.</p>
          </div>
        </section>
      </div>
    );
  }

  const completionScore = computeCompletion(data);
  const submitted = data.application_status && data.application_status !== "draft";

  return (
    <div className="min-h-screen">
      <SEO {...SEO_APPLY_MANAGER} />
      <PublicHeader />
      <section className="pt-28 pb-12 bg-[#F6F8FC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0" data-testid="apply-badge">Candidatura manager</Badge>
          <h1 className="mt-3 font-display text-3xl font-bold text-slate-900">Porta il tuo valore in più aziende.</h1>
          <p className="mt-2 text-slate-600">
            Completa il profilo. Salveremo le bozze. Quando sei pronto, invia la candidatura al team CXOORA.
          </p>

          {/* Progress + status banner */}
          <div className="mt-5 cxo-card p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Completamento profilo</p>
                <span className="cxo-tabular font-display font-bold text-slate-900" data-testid="completion-score">{completionScore}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#2D6BFF] to-[#1F9D6A] transition-all" style={{ width: `${completionScore}%` }} />
              </div>
            </div>
            {submitted && (
              <Badge className="bg-[#E9F8F1] text-[#1F9D6A] border-0" data-testid="application-status-badge">
                Stato: {data.application_status}
              </Badge>
            )}
          </div>

          <div className="mt-6 space-y-5">
            {/* === 1. IDENTITY & MEDIA === */}
            <SectionCard icon={UserIcon} title="Identità e media" subtitle="Foto, CV e lingue parlate.">
              <AvatarUpload
                value={data.photo}
                name={user?.full_name || data.title}
                onChange={(v) => setData({ ...data, photo: v })}
              />
              <div className="mt-5">
                <Label className="mb-2 block">CV (opzionale ma consigliato)</Label>
                <CvUpload value={data.cv_url} onChange={(v) => setData({ ...data, cv_url: v })} />
              </div>
              <div className="mt-5">
                <Label className="mb-2 block">Lingue parlate</Label>
                <div className="flex flex-wrap gap-2" data-testid="languages-chips">
                  {LANGUAGES_AVAILABLE.map((lng) => {
                    const sel = (data.languages || []).includes(lng);
                    return (
                      <Chip key={lng} active={sel} onClick={() => toggleArr("languages", lng)} testId={`lang-chip-${lng.toLowerCase().replace(/\s/g, "-")}`}>
                        {lng}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            </SectionCard>

            {/* === 2. PROFESSIONAL PROFILE === */}
            <SectionCard icon={BriefcaseBusiness} title="Profilo professionale" subtitle="Posizionamento, ruoli, settori e competenze.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Titolo professionale">
                  <Input
                    value={data.title || ""}
                    onChange={(e) => setData({ ...data, title: e.target.value })}
                    placeholder="Es. CFO con esperienza in PMI manifatturiere"
                    data-testid="input-title"
                  />
                </FormField>
                <FormField label="Ruolo principale *">
                  <Select value={data.primary_role || ""} onValueChange={(v) => setData({ ...data, primary_role: v })}>
                    <SelectTrigger data-testid="select-primary-role"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
                    <SelectContent>
                      {MANAGER_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Bio (max 500 caratteri)" className="sm:col-span-2">
                  <Textarea
                    maxLength={500}
                    rows={3}
                    value={data.bio || ""}
                    onChange={(e) => setData({ ...data, bio: e.target.value })}
                    placeholder="Descriviti in poche righe..."
                    data-testid="textarea-bio"
                  />
                  <p className="text-xs text-slate-400 mt-1 text-right">{(data.bio || "").length}/500</p>
                </FormField>
              </div>

              <div className="mt-5">
                <Label className="mb-2 block">Ruoli secondari <span className="text-xs text-slate-500">(opzionale, max {MAX_SECONDARY})</span></Label>
                <div className="flex flex-wrap gap-2" data-testid="secondary-roles-chips">
                  {MANAGER_ROLES.filter((r) => r !== data.primary_role).map((r) => {
                    const sel = (data.secondary_roles || []).includes(r);
                    return (
                      <Chip key={r} active={sel} onClick={() => toggleSecondary(r)}>
                        {r}
                      </Chip>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5">
                <Label className="mb-2 block">Dimensioni aziende gestite</Label>
                <div className="flex flex-wrap gap-2" data-testid="company-sizes-chips">
                  {COMPANY_SIZES.map((s) => {
                    const sel = (data.company_sizes_handled || []).includes(s);
                    return (
                      <Chip key={s} active={sel} onClick={() => toggleArr("company_sizes_handled", s)}>
                        {s}
                      </Chip>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Settori (separati da virgola)">
                  <Input
                    value={(data.industries || []).join(", ")}
                    onChange={(e) => setData({ ...data, industries: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="manifatturiero, servizi, food"
                    data-testid="input-industries"
                  />
                </FormField>
                <FormField label="Competenze (separate da virgola)">
                  <Input
                    value={(data.skills || []).join(", ")}
                    onChange={(e) => setData({ ...data, skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Controllo di gestione, Cash flow, KPI"
                    data-testid="input-skills"
                  />
                </FormField>
                <FormField label="Problemi risolti (separati da virgola)" className="sm:col-span-2">
                  <Input
                    value={(data.problems_solved || []).join(", ")}
                    onChange={(e) => setData({ ...data, problems_solved: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Margini in caduta, KPI mancanti, deleghe assenti"
                    data-testid="input-problems"
                  />
                </FormField>
              </div>
            </SectionCard>

            {/* === 3. EXPERIENCES === */}
            <SectionCard icon={GraduationCap} title="Esperienze chiave" subtitle="Racconta i risultati che ti rappresentano. Anche solo 2-3 esperienze fanno la differenza.">
              <FormField label="Sintesi delle tue esperienze principali (max 1500 caratteri)">
                <Textarea
                  rows={4}
                  maxLength={1500}
                  value={data.experiences_summary || ""}
                  onChange={(e) => setData({ ...data, experiences_summary: e.target.value })}
                  placeholder="Es. Ho guidato 12 PMI manifatturiere nella riorganizzazione del controllo di gestione, riducendo i tempi di chiusura mensile del 60% e aumentando l'EBITDA medio del 4%..."
                  data-testid="textarea-experiences-summary"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{(data.experiences_summary || "").length}/1500</p>
              </FormField>

              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <Label className="m-0">Esperienze chiave strutturate <span className="text-xs text-slate-500">(opzionale, max {MAX_EXPERIENCES})</span></Label>
                  <Button type="button" size="sm" variant="outline" onClick={addExperience} disabled={(data.case_studies || []).length >= MAX_EXPERIENCES} data-testid="add-experience-btn">
                    <Plus size={14} className="mr-1" /> Aggiungi
                  </Button>
                </div>
                <div className="space-y-3">
                  {(data.case_studies || []).map((exp, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-200 p-4 bg-white" data-testid={`experience-row-${idx}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Esperienza #{idx + 1}</p>
                        <button
                          type="button"
                          onClick={() => removeExperience(idx)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`remove-experience-${idx}`}
                          aria-label="Rimuovi esperienza"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FormField label="Azienda / Cliente">
                          <Input value={exp.company || ""} onChange={(e) => updateExperience(idx, "company", e.target.value)} placeholder="Es. Manifattura Rossi Spa" data-testid={`exp-company-${idx}`} />
                        </FormField>
                        <FormField label="Ruolo">
                          <Input value={exp.role || ""} onChange={(e) => updateExperience(idx, "role", e.target.value)} placeholder="Es. Fractional CFO" data-testid={`exp-role-${idx}`} />
                        </FormField>
                        <FormField label="Settore">
                          <Input value={exp.sector || ""} onChange={(e) => updateExperience(idx, "sector", e.target.value)} placeholder="Es. manifatturiero" data-testid={`exp-sector-${idx}`} />
                        </FormField>
                        <div className="grid grid-cols-2 gap-2">
                          <FormField label="Da (anno)">
                            <Input type="number" value={exp.from || ""} onChange={(e) => updateExperience(idx, "from", e.target.value)} placeholder="2022" data-testid={`exp-from-${idx}`} />
                          </FormField>
                          <FormField label="A (anno o 'in corso')">
                            <Input value={exp.to || ""} onChange={(e) => updateExperience(idx, "to", e.target.value)} placeholder="2024 / in corso" data-testid={`exp-to-${idx}`} />
                          </FormField>
                        </div>
                        <FormField label="Risultato chiave (max 200 caratteri)" className="sm:col-span-2">
                          <Input maxLength={200} value={exp.result || ""} onChange={(e) => updateExperience(idx, "result", e.target.value)} placeholder="Es. +12% EBITDA in 18 mesi, riduzione costi operativi -8%" data-testid={`exp-result-${idx}`} />
                        </FormField>
                      </div>
                    </div>
                  ))}
                  {(data.case_studies || []).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-300 rounded-lg">Nessuna esperienza strutturata aggiunta.</p>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* === 4. AVAILABILITY & RATES === */}
            <SectionCard icon={Award} title="Disponibilità e tariffe" subtitle="Indica quanti giorni puoi dedicare e in che modalità lavori.">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <FormField label="Anni totali">
                  <Input type="number" value={data.years_experience || ""} onChange={(e) => setData({ ...data, years_experience: parseInt(e.target.value) || 0 })} data-testid="input-years-experience" />
                </FormField>
                <FormField label="Anni manageriali">
                  <Input type="number" value={data.years_managerial || ""} onChange={(e) => setData({ ...data, years_managerial: parseInt(e.target.value) || 0 })} data-testid="input-years-managerial" />
                </FormField>
                <FormField label="Giorni/mese disponibili">
                  <Input type="number" max="20" value={data.monthly_availability_days || ""} onChange={(e) => setData({ ...data, monthly_availability_days: parseInt(e.target.value) || 0 })} data-testid="input-monthly-days" />
                </FormField>
                <FormField label="Max clienti contemporanei">
                  <Input type="number" max="5" value={data.max_concurrent_clients || ""} onChange={(e) => setData({ ...data, max_concurrent_clients: parseInt(e.target.value) || 0 })} data-testid="input-max-clients" />
                </FormField>
                <FormField label="Tariffa min/giorno (€)">
                  <Input type="number" value={data.daily_rate_min || ""} onChange={(e) => setData({ ...data, daily_rate_min: e.target.value })} data-testid="input-rate-min" />
                </FormField>
                <FormField label="Tariffa max/giorno (€)">
                  <Input type="number" value={data.daily_rate_max || ""} onChange={(e) => setData({ ...data, daily_rate_max: e.target.value })} data-testid="input-rate-max" />
                </FormField>
                <FormField label="Trasferte">
                  <Select value={data.travel_availability || ""} onValueChange={(v) => setData({ ...data, travel_availability: v })}>
                    <SelectTrigger data-testid="select-travel"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{["disponibile", "limitata", "no"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </FormField>
                <FormField label="LinkedIn">
                  <Input value={data.linkedin || ""} onChange={(e) => setData({ ...data, linkedin: e.target.value })} placeholder="https://linkedin.com/in/..." data-testid="input-linkedin" />
                </FormField>
              </div>
              <div className="mt-4">
                <Label className="mb-2 block">Modalità di lavoro</Label>
                <div className="flex flex-wrap gap-2" data-testid="work-modes-chips">
                  {["presenza", "ibrida", "remota"].map((m) => (
                    <Chip key={m} active={(data.work_modes || []).includes(m)} onClick={() => toggleArr("work_modes", m)}>
                      {m}
                    </Chip>
                  ))}
                </div>
              </div>
              <FormField label="Sito web" className="mt-4">
                <Input value={data.website || ""} onChange={(e) => setData({ ...data, website: e.target.value })} placeholder="https://..." data-testid="input-website" />
              </FormField>
            </SectionCard>

            {/* === 5. GEOGRAPHIC COVERAGE === */}
            <SectionCard icon={MapPin} title="Copertura geografica" subtitle="Dove sei e dove puoi operare.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Città di residenza">
                  <Input value={data.city || ""} onChange={(e) => setData({ ...data, city: e.target.value })} placeholder="Es. Milano" data-testid="input-city" />
                </FormField>
                <FormField label="Regione di residenza">
                  <Select value={data.region || ""} onValueChange={(v) => setData({ ...data, region: v })}>
                    <SelectTrigger data-testid="select-region"><SelectValue placeholder="Seleziona regione" /></SelectTrigger>
                    <SelectContent>
                      {ITALIAN_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              <div className="mt-5">
                <Label className="mb-2 block">Aree di consulenza</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Chip
                    active={(data.coverage_areas || []).includes(COVERAGE_ALL_ITALY)}
                    onClick={() => toggleCoverage(COVERAGE_ALL_ITALY)}
                    testId="coverage-all-italy"
                    icon={<Globe size={12} />}
                  >
                    Tutta Italia
                  </Chip>
                  <Chip
                    active={(data.coverage_areas || []).includes(COVERAGE_ABROAD)}
                    onClick={() => toggleCoverage(COVERAGE_ABROAD)}
                    testId="coverage-abroad"
                  >
                    Disponibile all&apos;estero
                  </Chip>
                </div>
                <p className="text-xs text-slate-500 mb-2">Oppure seleziona regioni specifiche:</p>
                <div className="flex flex-wrap gap-2" data-testid="coverage-regions">
                  {ITALIAN_REGIONS.map((r) => {
                    const sel = (data.coverage_areas || []).includes(r);
                    const allItaly = (data.coverage_areas || []).includes(COVERAGE_ALL_ITALY);
                    return (
                      <Chip
                        key={r}
                        active={sel}
                        disabled={allItaly}
                        onClick={() => toggleCoverage(r)}
                        testId={`coverage-region-${r.toLowerCase().replace(/[\s'-]/g, "-")}`}
                      >
                        {r}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            </SectionCard>

            {/* === 6. REFERENCES === */}
            <SectionCard icon={UsersIcon} title="Referenze" subtitle="Opzionali. Le referenze potrebbero essere verificate dal team CXOORA.">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">Aggiungi fino a {MAX_REFERENCES} referenze professionali.</p>
                <Button type="button" size="sm" variant="outline" onClick={addReference} disabled={(data.references || []).length >= MAX_REFERENCES} data-testid="add-reference-btn">
                  <Plus size={14} className="mr-1" /> Aggiungi referenza
                </Button>
              </div>
              <div className="space-y-3">
                {(data.references || []).map((ref, idx) => (
                  <div key={idx} className="rounded-xl border border-slate-200 p-4 bg-white" data-testid={`reference-row-${idx}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Referenza #{idx + 1}</p>
                      <button type="button" onClick={() => removeReference(idx)} className="text-slate-400 hover:text-red-500" aria-label="Rimuovi referenza" data-testid={`remove-reference-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <FormField label="Nome e cognome">
                        <Input value={ref.name || ""} onChange={(e) => updateReference(idx, "name", e.target.value)} placeholder="Mario Rossi" data-testid={`ref-name-${idx}`} />
                      </FormField>
                      <FormField label="Ruolo / Azienda">
                        <Input value={ref.role_company || ""} onChange={(e) => updateReference(idx, "role_company", e.target.value)} placeholder="CEO @ Rossi Spa" data-testid={`ref-role-${idx}`} />
                      </FormField>
                      <FormField label="Email di contatto">
                        <Input type="email" value={ref.email || ""} onChange={(e) => updateReference(idx, "email", e.target.value)} placeholder="mario@rossispa.it" data-testid={`ref-email-${idx}`} />
                      </FormField>
                    </div>
                  </div>
                ))}
                {(data.references || []).length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-300 rounded-lg">Nessuna referenza aggiunta.</p>
                )}
              </div>
            </SectionCard>

            {/* === 7. CONSENTS === */}
            <SectionCard icon={FileSignature} title="Consensi obbligatori" subtitle="Necessari per inviare la candidatura.">
              <div className="space-y-3">
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <Checkbox className="mt-0.5" checked={data.consent_privacy} onCheckedChange={(v) => setData({ ...data, consent_privacy: !!v })} data-testid="consent-privacy" />
                  <span>Confermo di aver letto e accettato la <Link to="/privacy" className="text-[#2D6BFF] underline">Privacy Policy</Link>.</span>
                </label>
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <Checkbox className="mt-0.5" checked={data.consent_ethics} onCheckedChange={(v) => setData({ ...data, consent_ethics: !!v })} data-testid="consent-ethics" />
                  <span>Accetto il <Link to="/codice-etico" className="text-[#2D6BFF] underline">Codice etico CXOORA</Link>.</span>
                </label>
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <Checkbox className="mt-0.5" checked={data.conflict_declaration} onCheckedChange={(v) => setData({ ...data, conflict_declaration: !!v })} data-testid="consent-conflict" />
                  <span>Dichiaro l&apos;assenza di conflitti di interesse rilevanti o mi impegno a dichiararli prima di ogni incarico.</span>
                </label>
              </div>
            </SectionCard>

            {/* === ACTIONS === */}
            <div className="cxo-card p-5 flex flex-col sm:flex-row items-center justify-between gap-3 sticky bottom-3 z-10 bg-white shadow-lg">
              <p className="text-xs text-slate-500">
                💡 Le bozze vengono salvate sul tuo profilo. Puoi tornare in qualunque momento.
              </p>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => save()} disabled={savingDraft || loading} data-testid="save-draft-btn">
                  {savingDraft && <Loader2 size={16} className="mr-2 animate-spin" />}
                  Salva bozza
                </Button>
                <Button
                  onClick={submit}
                  disabled={loading || submitted}
                  className="bg-[#1F9D6A] hover:bg-[#198555] text-white"
                  data-testid="submit-application-btn"
                >
                  {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
                  {submitted ? "Candidatura inviata" : "Invia candidatura"}
                  {!submitted && <ArrowRight size={16} className="ml-2" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// ===== UI helpers =====

const FormField = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</Label>
    {children}
  </div>
);

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <div className="cxo-card p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="rounded-xl bg-[#EAF0FF] p-2.5 text-[#2D6BFF] shrink-0">
        <Icon size={20} />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Chip = ({ active, onClick, children, disabled = false, testId, icon }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition ${
      disabled
        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
        : active
        ? "bg-[#2D6BFF] text-white border-[#2D6BFF] hover:bg-[#1F57F2]"
        : "bg-white text-slate-700 border-slate-200 hover:border-[#2D6BFF] hover:text-[#2D6BFF]"
    }`}
  >
    {icon}
    {children}
  </button>
);

// Compute % of profile completeness (visual only)
const computeCompletion = (d) => {
  const checks = [
    !!d.title,
    !!d.bio && d.bio.length > 30,
    !!d.primary_role,
    (d.industries || []).length > 0,
    (d.skills || []).length > 0,
    !!d.years_experience,
    !!d.monthly_availability_days,
    !!d.daily_rate_min,
    !!d.city,
    !!d.region,
    (d.coverage_areas || []).length > 0,
    (d.work_modes || []).length > 0,
    !!d.photo,
    !!d.cv_url,
    (d.case_studies || []).length > 0 || (d.experiences_summary || "").length > 50,
    (d.languages || []).length > 0,
    d.consent_privacy && d.consent_ethics,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
};
